import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Trash, Receipt, Scales } from "@phosphor-icons/react";
import { RedFlagList } from "@/components/app/Vendor360Drawer";

const MS_TONE = {
  pending: "bg-slate-100 text-slate-600", due: "bg-amber-50 text-amber-700",
  invoiced: "bg-blue-50 text-blue-700", paid: "bg-emerald-50 text-emerald-700",
};
const SC_TONE = {
  draft: "bg-slate-100 text-slate-600", active: "bg-blue-50 text-blue-700",
  completed: "bg-emerald-50 text-emerald-700", terminated: "bg-red-50 text-red-700",
};

function Chip({ v, map }) {
  return <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${map[v] || "bg-slate-100 text-slate-600"}`}>{v}</span>;
}

function Money({ v, bold }) {
  return <span className={`tabular-nums font-mono-alt ${bold ? "font-semibold" : ""}`}>{formatCurrency(v || 0)}</span>;
}

// ── Create dialog ────────────────────────────────────────────────────────
function CreateAgreementDialog({ open, onClose, projects, onCreated }) {
  const blank = {
    title: "", vendor_name: "", project_id: "", scope: "", technical_requirements: "",
    original_value: 0, advance_payment: 0, retention_pct: 0,
    taxes_note: "", penalties: "", insurance: "", guarantees: "", termination_conditions: "",
    descon_manager: "", subcontractor_rep: "", start_date: "", end_date: "", status: "draft",
  };
  const [form, setForm] = useState(blank);
  const [deliverables, setDeliverables] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [preaward, setPreaward] = useState(null);
  useEffect(() => { if (open) { setForm(blank); setDeliverables([]); setMilestones([]); setPreaward(null); } }, [open]); // eslint-disable-line

  useEffect(() => {
    const name = (form.vendor_name || "").trim();
    if (name.length < 3) { setPreaward(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/vendors/preaward-check", { params: { name } });
        setPreaward(data);
      } catch { setPreaward(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.vendor_name]);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.vendor_name.trim()) return toast.error("Subcontractor name is required");
    try {
      const body = { ...form, deliverables, milestones };
      if (preaward?.found) body.vendor_id = preaward.vendor_id;
      await api.post("/subcontracts", body);
      toast.success("Subcontract agreement created");
      onClose(); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sc-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New Subcontract Agreement</DialogTitle>
          <DialogDescription>Agreed terms, deliverables and payment milestone schedule.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-7">
            <Label>Title *</Label>
            <Input value={form.title} onChange={set("title")} data-testid="sc-title" />
          </div>
          <div className="col-span-5">
            <Label>Project</Label>
            <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="sc-project"><SelectValue placeholder="Link a project…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-7">
            <Label>Subcontractor *</Label>
            <Input value={form.vendor_name} onChange={set("vendor_name")} placeholder="Vendor / subcontractor name" data-testid="sc-vendor" />
            {preaward && (
              <div className="mt-1.5" data-testid="sc-preaward">
                {preaward.found
                  ? <RedFlagList flags={preaward.red_flags} testid="sc-preaward-flags" />
                  : <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600">{preaward.note}</div>}
              </div>
            )}
          </div>
          <div className="col-span-5">
            <Label>Subcontractor representative</Label>
            <Input value={form.subcontractor_rep} onChange={set("subcontractor_rep")} data-testid="sc-rep" />
          </div>
          <div className="col-span-12">
            <Label>Agreed scope</Label>
            <Textarea rows={2} value={form.scope} onChange={set("scope")} data-testid="sc-scope" />
          </div>
          <div className="col-span-12">
            <Label>Technical requirements</Label>
            <Textarea rows={2} value={form.technical_requirements} onChange={set("technical_requirements")} />
          </div>
          <div className="col-span-3"><Label>Contract value (USD) *</Label>
            <Input type="number" step="0.01" value={form.original_value} onChange={(e) => setForm({ ...form, original_value: parseFloat(e.target.value || 0) })} data-testid="sc-value" /></div>
          <div className="col-span-3"><Label>Advance payment</Label>
            <Input type="number" step="0.01" value={form.advance_payment} onChange={(e) => setForm({ ...form, advance_payment: parseFloat(e.target.value || 0) })} data-testid="sc-advance" /></div>
          <div className="col-span-3"><Label>Retention %</Label>
            <Input type="number" step="0.5" value={form.retention_pct} onChange={(e) => setForm({ ...form, retention_pct: parseFloat(e.target.value || 0) })} data-testid="sc-retention" /></div>
          <div className="col-span-3"><Label>Taxes</Label>
            <Input value={form.taxes_note} onChange={set("taxes_note")} placeholder="e.g. 5% VAT incl." /></div>
          <div className="col-span-6"><Label>Penalties / liquidated damages</Label>
            <Input value={form.penalties} onChange={set("penalties")} placeholder="e.g. 0.5%/week delay, cap 10%" data-testid="sc-penalties" /></div>
          <div className="col-span-6"><Label>Insurance</Label>
            <Input value={form.insurance} onChange={set("insurance")} placeholder="e.g. CAR + third-party liability" /></div>
          <div className="col-span-6"><Label>Guarantees</Label>
            <Input value={form.guarantees} onChange={set("guarantees")} placeholder="e.g. 10% performance bond" /></div>
          <div className="col-span-6"><Label>Termination conditions</Label>
            <Input value={form.termination_conditions} onChange={set("termination_conditions")} /></div>
          <div className="col-span-4"><Label>DESCON manager</Label>
            <Input value={form.descon_manager} onChange={set("descon_manager")} /></div>
          <div className="col-span-4"><Label>Start date</Label>
            <Input type="date" value={form.start_date} onChange={set("start_date")} /></div>
          <div className="col-span-4"><Label>End date</Label>
            <Input type="date" value={form.end_date} onChange={set("end_date")} data-testid="sc-end-date" /></div>

          {/* Deliverables */}
          <div className="col-span-12">
            <div className="flex items-center justify-between">
              <Label>Agreed deliverables</Label>
              <Button type="button" size="sm" variant="outline" className="h-7"
                onClick={() => setDeliverables([...deliverables, { description: "", qty: 1, unit: "", status: "pending", progress_pct: 0 }])}
                data-testid="sc-add-deliverable"><Plus size={11} className="mr-1" /> Add</Button>
            </div>
            {deliverables.map((d, i) => (
              <div key={i} className="mt-1.5 grid grid-cols-12 gap-2">
                <Input className="col-span-7 h-8" placeholder="Deliverable description" value={d.description}
                  onChange={(e) => setDeliverables(deliverables.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                  data-testid={`sc-deliv-desc-${i}`} />
                <Input className="col-span-2 h-8" type="number" placeholder="Qty" value={d.qty}
                  onChange={(e) => setDeliverables(deliverables.map((x, j) => j === i ? { ...x, qty: parseFloat(e.target.value || 0) } : x))} />
                <Input className="col-span-2 h-8" placeholder="Unit" value={d.unit}
                  onChange={(e) => setDeliverables(deliverables.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} />
                <Button type="button" variant="outline" className="col-span-1 h-8" onClick={() => setDeliverables(deliverables.filter((_, j) => j !== i))}><Trash size={11} /></Button>
              </div>
            ))}
          </div>

          {/* Milestones */}
          <div className="col-span-12">
            <div className="flex items-center justify-between">
              <Label>Payment milestone schedule</Label>
              <Button type="button" size="sm" variant="outline" className="h-7"
                onClick={() => setMilestones([...milestones, { description: "", amount: 0, due_date: "" }])}
                data-testid="sc-add-milestone"><Plus size={11} className="mr-1" /> Add</Button>
            </div>
            {milestones.map((m, i) => (
              <div key={i} className="mt-1.5 grid grid-cols-12 gap-2">
                <Input className="col-span-6 h-8" placeholder="Milestone description" value={m.description}
                  onChange={(e) => setMilestones(milestones.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                  data-testid={`sc-ms-desc-${i}`} />
                <Input className="col-span-3 h-8" type="number" step="0.01" placeholder="Amount" value={m.amount}
                  onChange={(e) => setMilestones(milestones.map((x, j) => j === i ? { ...x, amount: parseFloat(e.target.value || 0) } : x))}
                  data-testid={`sc-ms-amount-${i}`} />
                <Input className="col-span-2 h-8" type="date" value={m.due_date}
                  onChange={(e) => setMilestones(milestones.map((x, j) => j === i ? { ...x, due_date: e.target.value } : x))}
                  data-testid={`sc-ms-due-${i}`} />
                <Button type="button" variant="outline" className="col-span-1 h-8" onClick={() => setMilestones(milestones.filter((_, j) => j !== i))}><Trash size={11} /></Button>
              </div>
            ))}
            {milestones.length > 0 && (
              <div className="mt-1 text-right text-[11px] text-slate-500">
                Milestones total: <Money v={milestones.reduce((s, m) => s + (m.amount || 0), 0)} bold />
                {" "}vs contract <Money v={form.original_value} bold />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="btn-primary-blue" onClick={submit} data-testid="sc-create-submit">Create agreement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Milestone → supplier bill dialog ─────────────────────────────────────
function MilestoneBillDialog({ sc, milestone, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    bill_number: "", bill_date: today,
    due_date: milestone?.due_date || today,
    total: milestone?.amount || 0,
  });
  if (!milestone) return null;
  const submit = async (force = false) => {
    try {
      await api.post("/finance/ap", {
        vendor_id: sc.vendor_id || "", vendor_name: sc.vendor_name,
        project_id: sc.project_id || "", project_name: sc.project_name || "",
        subcontract_id: sc.id, milestone_id: milestone.id,
        bill_number: form.bill_number, bill_date: form.bill_date, due_date: form.due_date,
        total: parseFloat(form.total || 0), category: "Subcontract works",
        notes: `Milestone: ${milestone.description} · ${sc.agreement_no}`, force,
      });
      toast.success("Supplier invoice recorded against the milestone");
      onClose(); onDone();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === "duplicate_suspected") {
        if (window.confirm(`${det.message}\nCreate anyway? (override is audit-logged)`)) return submit(true);
      } else toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={!!milestone} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="sc-ms-bill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-base">Record supplier invoice — milestone</DialogTitle>
          <DialogDescription>{milestone.description} · {sc.vendor_name}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Supplier bill no.</Label><Input value={form.bill_number} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} data-testid="sc-ms-bill-number" /></div>
          <div><Label>Amount (USD)</Label><Input type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="sc-ms-bill-total" /></div>
          <div><Label>Bill date</Label><Input type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} /></div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="btn-primary-blue" onClick={() => submit(false)} data-testid="sc-ms-bill-submit">Record invoice</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────
function AgreementDetail({ scId, onClose, canEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [billMs, setBillMs] = useState(null);
  const [voForm, setVoForm] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/subcontracts/${scId}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (scId) load(); }, [scId]); // eslint-disable-line

  if (!scId) return null;
  const c = data?.computed;

  const setProgress = async (dId, pct) => {
    try {
      const { data: d } = await api.patch(`/subcontracts/${scId}/deliverables/${dId}`, { progress_pct: pct });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const proposeVo = async () => {
    if (!voForm?.description?.trim()) return toast.error("Description required");
    try {
      await api.post(`/subcontracts/${scId}/variations`, voForm);
      toast.success("Variation proposed — awaiting approval");
      setVoForm(null); load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const decideVo = async (vid, decision) => {
    try {
      const { data: d } = await api.post(`/subcontracts/${scId}/variations/${vid}/decide`, { decision });
      setData(d); toast.success(`Variation ${decision}d`); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (status) => {
    try {
      const { data: d } = await api.patch(`/subcontracts/${scId}`, { status });
      setData(d); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!scId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="sc-detail-dialog">
        {!data ? <div className="p-6 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex flex-wrap items-center gap-2">
                <span className="font-mono-alt text-xs text-slate-400">{data.agreement_no}</span>
                {data.title}
                <Chip v={data.status} map={SC_TONE} />
              </DialogTitle>
              <DialogDescription>
                {data.vendor_name} {data.project_name && <>· {data.project_name}</>} {data.subcontractor_rep && <>· Rep: {data.subcontractor_rep}</>}
              </DialogDescription>
            </DialogHeader>

            {/* Comparison header */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="sc-comparisons">
              {[["Original", data.original_value], ["Variations", c.approved_variations_total],
                ["Revised value", c.revised_value], ["Billed", c.billed], ["Paid", c.paid],
                ["Retention held", c.retention_held], ["Remaining", c.remaining_value]].map(([l, v]) => (
                <div key={l} className="rounded-md border border-slate-200 bg-white p-2">
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
                  <div className="mt-0.5 font-display text-[13px] font-bold tabular-nums">{formatCurrency(v || 0)}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] text-slate-600">
              <span className="rounded-full bg-slate-100 px-2 py-0.5">Billed {c.comparisons.value_vs_billed.pct ?? "—"}% of contract</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5">Paid {c.comparisons.value_vs_paid.pct ?? "—"}%</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5">Deliverables {c.comparisons.deliverables.completed}/{c.comparisons.deliverables.agreed} · avg {c.comparisons.deliverables.avg_progress_pct}%</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5">Milestones paid {c.comparisons.schedule_vs_payments.paid}/{c.comparisons.schedule_vs_payments.milestones}{c.comparisons.schedule_vs_payments.overdue > 0 && <b className="text-red-600"> · {c.comparisons.schedule_vs_payments.overdue} overdue</b>}</span>
            </div>

            {/* Terms */}
            <section className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md border border-slate-200 bg-slate-50/50 p-3 text-xs sm:grid-cols-3" data-testid="sc-terms">
              {[["Advance payment", formatCurrency(data.advance_payment || 0)],
                ["Retention", `${data.retention_pct || 0}%`],
                ["Taxes", data.taxes_note], ["Penalties", data.penalties],
                ["Insurance", data.insurance], ["Guarantees", data.guarantees],
                ["Termination", data.termination_conditions],
                ["DESCON manager", data.descon_manager],
                ["Period", `${data.start_date ? formatDate(data.start_date) : "—"} → ${data.end_date ? formatDate(data.end_date) : "—"}`]].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{k}</div>
                  <div className="text-slate-800">{v || "—"}</div>
                </div>
              ))}
              {data.scope && <div className="col-span-2 sm:col-span-3"><div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Scope</div><div className="text-slate-800">{data.scope}</div></div>}
            </section>

            {/* Deliverables progress */}
            <section data-testid="sc-deliverables">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Agreed deliverables vs actual progress</h4>
              {(data.deliverables || []).length === 0 ? (
                <div className="mt-1.5 rounded-md border border-dashed border-slate-200 p-2.5 text-center text-xs text-slate-400">No itemised deliverables recorded.</div>
              ) : (
                <ul className="mt-1.5 space-y-1.5">
                  {data.deliverables.map((d) => (
                    <li key={d.id} className="flex items-center gap-3 rounded-md border border-slate-200 p-2 text-xs" data-testid={`sc-deliv-${d.id}`}>
                      <div className="min-w-0 flex-1">
                        <span className="font-medium text-slate-800">{d.description}</span>
                        {(d.qty || d.unit) && <span className="ml-1 text-slate-400">({d.qty} {d.unit})</span>}
                      </div>
                      <div className="h-1.5 w-28 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-blue-600" style={{ width: `${d.progress_pct || 0}%` }} />
                      </div>
                      <span className="w-9 text-right tabular-nums">{d.progress_pct || 0}%</span>
                      {canEdit && (
                        <Input className="h-7 w-16 text-xs" type="number" min="0" max="100" defaultValue={d.progress_pct || 0}
                          onBlur={(e) => { const v = parseFloat(e.target.value || 0); if (v !== (d.progress_pct || 0)) setProgress(d.id, v); }}
                          data-testid={`sc-deliv-progress-${d.id}`} />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Milestones */}
            <section data-testid="sc-milestones">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Payment milestone schedule vs payments made</h4>
              {(data.milestones || []).length === 0 ? (
                <div className="mt-1.5 rounded-md border border-dashed border-slate-200 p-2.5 text-center text-xs text-slate-400">No milestone schedule recorded.</div>
              ) : (
                <table className="mt-1.5 w-full text-xs">
                  <tbody>
                    {data.milestones.map((m) => (
                      <tr key={m.id} className="border-t border-slate-100" data-testid={`sc-ms-${m.id}`}>
                        <td className="py-1.5 pr-2">{m.description}</td>
                        <td className="py-1.5 pr-2 text-slate-500">{m.due_date ? formatDate(m.due_date) : "—"}</td>
                        <td className="py-1.5 pr-2 text-right"><Money v={m.amount} /></td>
                        <td className="py-1.5 pr-2"><Chip v={m.status} map={MS_TONE} /></td>
                        <td className="py-1.5 pr-2 text-right text-slate-500">
                          {m.billed != null && <>billed <Money v={m.billed} /> · paid <Money v={m.paid_amount || 0} /></>}
                        </td>
                        <td className="py-1.5 text-right">
                          {canEdit && (m.status === "pending" || m.status === "due") && (
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => setBillMs(m)} data-testid={`sc-ms-invoice-${m.id}`}>
                              <Receipt size={10} className="mr-1" /> Record invoice
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Variations */}
            <section data-testid="sc-variations">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Variations / change orders</h4>
                {canEdit && <Button size="sm" variant="outline" className="h-7" onClick={() => setVoForm({ description: "", value_delta: 0, time_impact_days: 0 })} data-testid="sc-vo-add-btn">Propose variation</Button>}
              </div>
              {voForm && (
                <div className="mt-2 grid grid-cols-12 gap-2 rounded-md border border-blue-100 bg-blue-50/50 p-3">
                  <Input className="col-span-6 h-8" placeholder="Description *" value={voForm.description} onChange={(e) => setVoForm({ ...voForm, description: e.target.value })} data-testid="sc-vo-desc" />
                  <Input className="col-span-2 h-8" type="number" step="0.01" placeholder="Value ±" value={voForm.value_delta} onChange={(e) => setVoForm({ ...voForm, value_delta: parseFloat(e.target.value || 0) })} data-testid="sc-vo-delta" />
                  <Input className="col-span-2 h-8" type="number" placeholder="Days ±" value={voForm.time_impact_days} onChange={(e) => setVoForm({ ...voForm, time_impact_days: parseInt(e.target.value || 0) })} />
                  <Button size="sm" className="col-span-2 h-8 btn-primary-blue" onClick={proposeVo} data-testid="sc-vo-submit">Submit</Button>
                </div>
              )}
              {(data.variations || []).length === 0 ? (
                <div className="mt-1.5 rounded-md border border-dashed border-slate-200 p-2.5 text-center text-xs text-slate-400">No variations — contract value unchanged from original.</div>
              ) : (
                <ul className="mt-1.5 space-y-1.5">
                  {data.variations.map((v) => (
                    <li key={v.id} className="flex items-center gap-2 rounded-md border border-slate-200 p-2 text-xs" data-testid={`sc-vo-${v.vo_no}`}>
                      <span className="font-mono-alt text-[10px] text-slate-400">{v.vo_no}</span>
                      <span className="min-w-0 flex-1">{v.description}</span>
                      <span className={`tabular-nums font-mono-alt ${v.value_delta < 0 ? "text-red-700" : "text-emerald-700"}`}>{v.value_delta > 0 ? "+" : ""}{formatCurrency(v.value_delta)}</span>
                      {v.time_impact_days !== 0 && <span className="text-slate-500">{v.time_impact_days > 0 ? "+" : ""}{v.time_impact_days}d</span>}
                      <Chip v={v.status} map={{ pending: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700" }} />
                      {v.status === "pending" && canEdit && (
                        <>
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => decideVo(v.id, "approve")} data-testid={`sc-vo-approve-${v.vo_no}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-red-700" onClick={() => decideVo(v.id, "reject")} data-testid={`sc-vo-reject-${v.vo_no}`}>Reject</Button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <DialogFooter className="flex-wrap gap-2">
              {canEdit && data.status === "draft" && <Button variant="outline" onClick={() => setStatus("active")} data-testid="sc-activate">Activate</Button>}
              {canEdit && data.status === "active" && <Button variant="outline" onClick={() => setStatus("completed")} data-testid="sc-complete">Mark completed</Button>}
              <Button variant="ghost" onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        )}
        <MilestoneBillDialog sc={data || {}} milestone={billMs} onClose={() => setBillMs(null)} onDone={() => { load(); onChanged(); }} />
      </DialogContent>
    </Dialog>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────
export default function SubcontractsPanel({ canEdit }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [{ data: sc }, { data: pj }] = await Promise.all([
        api.get("/subcontracts"), api.get("/projects"),
      ]);
      setItems(sc.items || []);
      setProjects(Array.isArray(pj) ? pj : pj.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="subcontracts-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-sm font-bold text-slate-900 flex items-center gap-1.5"><Scales size={15} /> Subcontract Agreements</h3>
          <p className="text-xs text-slate-500">Agreed terms, milestone schedules, variations and live agreed-vs-actual comparisons.</p>
        </div>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => setCreateOpen(true)} data-testid="sc-new-btn">
            <Plus size={13} className="mr-1" /> New agreement
          </Button>
        )}
      </div>
      {loading ? (
        <div className="p-8 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400" data-testid="sc-empty">
          <FileText size={22} className="mx-auto mb-2 text-slate-300" />
          No subcontract agreements yet. Create the first one to start tracking agreed terms vs actual delivery.
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2">Agreement</th><th className="px-3 py-2">Subcontractor</th>
                <th className="px-3 py-2">Project</th><th className="px-3 py-2 text-right">Original</th>
                <th className="px-3 py-2 text-right">Revised</th><th className="px-3 py-2 text-right">Billed</th>
                <th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50"
                    onClick={() => setDetailId(r.id)} data-testid={`sc-row-${r.agreement_no}`}>
                  <td className="px-4 py-2.5"><span className="font-mono-alt text-[10px] text-slate-400">{r.agreement_no}</span> <span className="font-semibold text-slate-900">{r.title}</span></td>
                  <td className="px-3 py-2.5">{r.vendor_name}</td>
                  <td className="px-3 py-2.5 text-slate-500">{r.project_name || "—"}</td>
                  <td className="px-3 py-2.5 text-right"><Money v={r.original_value} /></td>
                  <td className="px-3 py-2.5 text-right"><Money v={r.revised_value} bold /></td>
                  <td className="px-3 py-2.5 text-right"><Money v={r.billed} /></td>
                  <td className="px-3 py-2.5 text-right"><Money v={r.paid} /></td>
                  <td className="px-3 py-2.5"><Chip v={r.status} map={SC_TONE} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateAgreementDialog open={createOpen} onClose={() => setCreateOpen(false)} projects={projects} onCreated={load} />
      {detailId && <AgreementDetail scId={detailId} canEdit={canEdit} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}
